/**
 * LeaderboardManager - Manages both local and global high scores
 *
 * Extends the original HighScoreManager with global leaderboard support.
 * Falls back to local storage when the server is unavailable.
 */

import {
  leaderboardService,
  LeaderboardEntry,
  LeaderboardUpdateEvent,
  GameMetadata,
} from '../services/LeaderboardService';

export interface HighScore {
  score: number;
  date: string;
  timestamp: number;
  name?: string;
  rank?: number;
  isLocal?: boolean;
}

export interface GlobalLeaderboardState {
  entries: LeaderboardEntry[];
  playerRank: number | null;
  isConnected: boolean;
  lastUpdated: number;
}

type LeaderboardChangeHandler = (state: GlobalLeaderboardState) => void;

export class LeaderboardManager {
  private static readonly LOCAL_STORAGE_KEY = 'spaceInvaders2030_highScores';
  private static readonly PLAYER_NAME_KEY = 'spaceInvaders2030_playerName';
  private static readonly MAX_LOCAL_SCORES = 5;

  private static globalState: GlobalLeaderboardState = {
    entries: [],
    playerRank: null,
    isConnected: false,
    lastUpdated: 0,
  };

  private static changeHandlers: Set<LeaderboardChangeHandler> = new Set();
  private static unsubscribeWs: (() => void) | null = null;
  private static initialized = false;

  // Game session tracking for score validation
  private static gameStartTime: number | null = null;
  private static aliensDestroyedCount = 0;
  private static powerUpsCollectedCount = 0;
  private static wavesCompletedCount = 0;

  /**
   * Initialize the leaderboard manager and connect to WebSocket
   */
  static init(): void {
    if (this.initialized) {
      return;
    }

    this.initialized = true;

    // Subscribe to WebSocket updates
    this.unsubscribeWs = leaderboardService.subscribe((event) => {
      this.handleLeaderboardUpdate(event);
    });

    // Initial fetch of leaderboard
    this.refreshGlobalLeaderboard();
  }

  /**
   * Clean up resources
   */
  static destroy(): void {
    if (this.unsubscribeWs) {
      this.unsubscribeWs();
      this.unsubscribeWs = null;
    }
    leaderboardService.disconnect();
    this.initialized = false;
  }

  private static handleLeaderboardUpdate(event: LeaderboardUpdateEvent): void {
    this.globalState = {
      ...this.globalState,
      entries: event.data,
      isConnected: true,
      lastUpdated: Date.now(),
    };

    // Check if update contains current player's score
    if (event.newScore && event.newScore.id === leaderboardService.getPlayerId()) {
      this.globalState.playerRank = event.newScore.rank;
    }

    this.notifyChangeHandlers();
  }

  private static notifyChangeHandlers(): void {
    for (const handler of this.changeHandlers) {
      try {
        handler(this.globalState);
      } catch (err) {
        console.error('Error in leaderboard change handler:', err);
      }
    }
  }

  /**
   * Subscribe to leaderboard changes
   */
  static subscribe(handler: LeaderboardChangeHandler): () => void {
    this.changeHandlers.add(handler);

    // Initialize if not already done
    if (!this.initialized) {
      this.init();
    }

    // Send current state immediately
    handler(this.globalState);

    return () => {
      this.changeHandlers.delete(handler);
    };
  }

  /**
   * Refresh global leaderboard from server
   */
  static async refreshGlobalLeaderboard(): Promise<LeaderboardEntry[]> {
    try {
      const entries = await leaderboardService.getLeaderboard(10);
      this.globalState = {
        ...this.globalState,
        entries,
        isConnected: true,
        lastUpdated: Date.now(),
      };
      this.notifyChangeHandlers();
      return entries;
    } catch (err) {
      console.error('Failed to refresh global leaderboard:', err);
      this.globalState.isConnected = false;
      this.notifyChangeHandlers();
      return [];
    }
  }

  /**
   * Get the global leaderboard state
   */
  static getGlobalState(): GlobalLeaderboardState {
    return { ...this.globalState };
  }

  /**
   * Get player's name from storage
   */
  static getPlayerName(): string {
    try {
      return localStorage.getItem(this.PLAYER_NAME_KEY) || 'Anonymous';
    } catch {
      return 'Anonymous';
    }
  }

  /**
   * Set player's name
   */
  static setPlayerName(name: string): void {
    try {
      localStorage.setItem(this.PLAYER_NAME_KEY, name.slice(0, 20));
    } catch (err) {
      console.error('Error saving player name:', err);
    }
  }

  // ========================================
  // Local Storage Methods (fallback/offline)
  // ========================================

  /**
   * Get all high scores from localStorage
   */
  static getLocalHighScores(): HighScore[] {
    try {
      const stored = localStorage.getItem(this.LOCAL_STORAGE_KEY);
      if (!stored) {
        return [];
      }
      const scores = JSON.parse(stored) as HighScore[];
      return scores
        .map((s) => ({ ...s, isLocal: true }))
        .sort((a, b) => b.score - a.score)
        .slice(0, this.MAX_LOCAL_SCORES);
    } catch (error) {
      console.error('Error reading local high scores:', error);
      return [];
    }
  }

  /**
   * Add a score to local storage
   */
  private static addLocalScore(score: number, name?: string): boolean {
    try {
      const scores = this.getLocalHighScores();
      const newScore: HighScore = {
        score,
        name: name || this.getPlayerName(),
        date: new Date().toLocaleDateString(),
        timestamp: Date.now(),
        isLocal: true,
      };

      scores.push(newScore);
      scores.sort((a, b) => b.score - a.score);
      const topScores = scores.slice(0, this.MAX_LOCAL_SCORES);

      localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(topScores));

      return topScores.some((s) => s.timestamp === newScore.timestamp);
    } catch (error) {
      console.error('Error saving local high score:', error);
      return false;
    }
  }

  /**
   * Check if a score would be a local high score
   */
  static isLocalHighScore(score: number): boolean {
    const scores = this.getLocalHighScores();
    if (scores.length < this.MAX_LOCAL_SCORES) {
      return score > 0;
    }
    return score > scores[scores.length - 1].score;
  }

  /**
   * Get the highest local score
   */
  static getTopLocalScore(): number {
    const scores = this.getLocalHighScores();
    return scores.length > 0 ? scores[0].score : 0;
  }

  // ========================================
  // Combined Methods (global + local)
  // ========================================

  /**
   * Submit a score - tries global first, falls back to local
   */
  static async submitScore(score: number): Promise<{
    isHighScore: boolean;
    rank: number | null;
    isGlobal: boolean;
  }> {
    const playerName = this.getPlayerName();
    const gameMetadata = this.getGameMetadata();

    // Always save locally
    const isLocalHighScore = this.addLocalScore(score, playerName);

    // Reset game tracking after getting metadata
    this.resetGameTracking();

    // Try to submit to global leaderboard
    try {
      const result = await leaderboardService.submitScore(score, playerName, gameMetadata);

      if (result.success) {
        this.globalState.playerRank = result.rank ?? null;
        this.globalState.isConnected = true;
        this.notifyChangeHandlers();

        return {
          isHighScore: true,
          rank: result.rank ?? null,
          isGlobal: true,
        };
      } else if (result.error) {
        // Log validation errors but still save locally
        console.warn('Score submission rejected:', result.error);
      }
    } catch (err) {
      console.error('Failed to submit score to global leaderboard:', err);
    }

    // Fall back to local
    return {
      isHighScore: isLocalHighScore,
      rank: null,
      isGlobal: false,
    };
  }

  /**
   * Get combined high scores - global if available, otherwise local
   */
  static getHighScores(): HighScore[] {
    // If we have global scores, use those
    if (this.globalState.entries.length > 0) {
      return this.globalState.entries.map((entry) => ({
        score: entry.score,
        date: entry.date,
        timestamp: entry.timestamp,
        name: entry.name,
        rank: entry.rank,
        isLocal: false,
      }));
    }

    // Fall back to local scores
    return this.getLocalHighScores();
  }

  /**
   * Get the top score (global or local)
   */
  static getTopScore(): number {
    if (this.globalState.entries.length > 0) {
      return this.globalState.entries[0]?.score || 0;
    }
    return this.getTopLocalScore();
  }

  /**
   * Check if online/connected to global leaderboard
   */
  static isOnline(): boolean {
    return this.globalState.isConnected;
  }

  /**
   * Get current player's global rank
   */
  static getPlayerRank(): number | null {
    return this.globalState.playerRank;
  }

  /**
   * Clear all local high scores (for testing/debugging)
   */
  static clearLocalHighScores(): void {
    try {
      localStorage.removeItem(this.LOCAL_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing local high scores:', error);
    }
  }

  // ========================================
  // Game Session Tracking (for validation)
  // ========================================

  /**
   * Start a new game - initializes tracking and creates server session
   */
  static async startGame(): Promise<void> {
    this.gameStartTime = Date.now();
    this.aliensDestroyedCount = 0;
    this.powerUpsCollectedCount = 0;
    this.wavesCompletedCount = 0;

    // Create server-side session for validation
    await leaderboardService.startGameSession();
  }

  /**
   * Track alien destruction
   */
  static recordAlienDestroyed(): void {
    this.aliensDestroyedCount++;
  }

  /**
   * Track power-up collection
   */
  static recordPowerUpCollected(): void {
    this.powerUpsCollectedCount++;
  }

  /**
   * Track wave completion
   */
  static recordWaveCompleted(): void {
    this.wavesCompletedCount++;
  }

  /**
   * Get game metadata for score submission
   */
  static getGameMetadata(): GameMetadata | undefined {
    if (this.gameStartTime === null) {
      return undefined;
    }

    return {
      gameDuration: Date.now() - this.gameStartTime,
      aliensDestroyed: this.aliensDestroyedCount,
      wavesCompleted: this.wavesCompletedCount,
      powerUpsCollected: this.powerUpsCollectedCount,
    };
  }

  /**
   * Reset game tracking state
   */
  static resetGameTracking(): void {
    this.gameStartTime = null;
    this.aliensDestroyedCount = 0;
    this.powerUpsCollectedCount = 0;
    this.wavesCompletedCount = 0;
  }
}

// Initialize on module load if in browser
if (typeof window !== 'undefined') {
  // Delay initialization to avoid blocking main thread
  setTimeout(() => {
    LeaderboardManager.init();
  }, 100);
}
